<template>
  <div class="tichete-page">
    <!-- Hero -->
    <div class="hero">
      <div class="hero-txt">
        <div class="hero-eyebrow">PORTAL HR</div>
        <h1 class="hero-title">Tichete HR</h1>
        <p class="hero-sub">Cereri de adeverințe și documente către HR. Urmărește starea și răspunsul.</p>
      </div>
      <v-btn class="btn-nou" prepend-icon="mdi-plus" @click="dialogTicket = true">Tichet nou</v-btn>
    </div>

    <!-- Grid -->
    <div class="grid-card">
      <div class="grid-head">
        <v-icon size="18" color="#ff5722" class="mr-2">mdi-ticket-confirmation-outline</v-icon>
        <span>Tichetele mele</span>
      </div>
      <DxDataGrid
        :data-source="dataSource"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :hover-state-enabled="true"
        :row-alternation-enabled="false"
        no-data-text="Nu ai niciun tichet."
      >
        <DxColumn data-field="TicketNr" caption="Număr" :width="150" cell-template="nrCell" />
        <DxColumn data-field="DataCreare" caption="Deschis" data-type="datetime" format="dd.MM.yyyy HH:mm" :width="160" />
        <DxColumn data-field="StatusTichet.Name" caption="Stare" :width="150" :allow-filtering="false" />
        <DxColumn data-field="PrioritateTichet.Name" caption="Prioritate" :width="120" :allow-filtering="false" />
        <DxColumn data-field="UserMessage" caption="Mesaj" :min-width="220" />
        <DxColumn data-field="HRMessage" caption="Răspuns HR" :min-width="200" cell-template="raspunsCell" />
        <DxColumn data-field="DataInchidere" caption="Închis" data-type="date" format="dd.MM.yyyy" :width="130" cell-template="inchisCell" />

        <template #nrCell="{ data }">
          <span class="nr-badge">{{ data.data.TicketNr || '—' }}</span>
        </template>
        <template #raspunsCell="{ data }">
          <span :class="{ 'text-muted': !data.data.HRMessage }">{{ data.data.HRMessage || 'În așteptare…' }}</span>
        </template>
        <template #inchisCell="{ data }">
          <span v-if="data.data.DataInchidere" class="stare-pill inchis"><span class="dot" />Închis</span>
          <span v-else class="stare-pill deschis"><span class="dot" />Deschis</span>
        </template>

        <DxPaging :page-size="12" />
        <DxSorting mode="single" />
        <DxFilterRow :visible="true" />
      </DxDataGrid>
    </div>

    <!-- Dialog tichet nou -->
    <v-dialog v-model="dialogTicket" max-width="520" persistent>
      <v-card rounded="xl" class="dlg-card">
        <div class="dlg-head">
          <div class="dlg-ic"><v-icon color="#fff" size="20">mdi-ticket-outline</v-icon></div>
          <div>
            <div class="dlg-title">Tichet HR nou</div>
            <div class="dlg-desc">Cere o adeverință sau un document de la HR.</div>
          </div>
          <v-btn icon="mdi-close" variant="text" color="#fff" size="small" @click="dialogTicket = false" />
        </div>
        <v-card-text class="pa-5">
          <v-form v-model="ticketValid" ref="ticketForm">
            <v-select
              v-model="ticketForm.DocumentTypeID" :items="tipuriDoc" item-title="Denumire" item-value="ID"
              label="Tip document" variant="outlined" density="comfortable" class="mb-4" :rules="[required]" :loading="loadingTipuri" hide-details="auto" />
            <v-select
              v-model="ticketForm.PriorityTypesID" :items="prioritati" item-title="Name" item-value="ID"
              label="Prioritate" variant="outlined" density="comfortable" class="mb-4" hide-details="auto" clearable />
            <v-textarea
              v-model="ticketForm.UserMessage" label="Mesaj / Detalii" variant="outlined" density="comfortable" rows="3" :rules="[required]" hide-details="auto" />
          </v-form>
          <v-alert v-if="ticketError" type="error" variant="tonal" density="compact" class="mt-4">{{ ticketError }}</v-alert>
        </v-card-text>
        <v-card-actions class="dlg-foot">
          <v-btn variant="text" @click="dialogTicket = false">Anulează</v-btn>
          <v-spacer />
          <v-btn class="btn-nou px-6" :loading="ticketLoading" :disabled="!ticketValid" @click="submitTicket">
            <v-icon start>mdi-send</v-icon>Trimite
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSorting } from 'devextreme-vue/data-grid'
import DataSource from 'devextreme/data/data_source'
import PORTAL_Tickets from '@/services/Portal/PORTAL_Tickets'
import http from "@/settings/apiSettings"

export default defineComponent({
  name: 'Tichete',
  components: { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSorting },
  data() {
    return {
      dataSource: new DataSource({ store: PORTAL_Tickets.getODataStore(), expand: ['StatusTichet', 'PrioritateTichet'], sort: [{ selector: 'DataCreare', desc: true }] }),
      dialogTicket: false,
      ticketValid: false,
      ticketLoading: false,
      loadingTipuri: false,
      ticketError: '',
      tipuriDoc: [] as any[],
      prioritati: [] as any[],
      ticketForm: { DocumentTypeID: '', PriorityTypesID: null as string | null, UserMessage: '' }
    }
  },
  async mounted() {
    this.loadingTipuri = true;
    const authHeader = { Authorization: "Bearer " + localStorage.getItem("ERA_Jwt") };
    try {
      const [respTip, respPrio] = await Promise.all([
        http.clientPortalHROdata.get('/PORTAL_DocumentTypes', { headers: authHeader }),
        http.clientPortalHROdata.get('/PORTAL_PriorityTypes?$orderby=PriorityIndex', { headers: authHeader }),
      ]);
      this.tipuriDoc = respTip.data?.value ?? [];
      this.prioritati = respPrio.data?.value ?? [];
    } catch { this.tipuriDoc = []; this.prioritati = []; }
    finally { this.loadingTipuri = false; }
  },
  methods: {
    required(v: any) { return !!v || 'Câmp obligatoriu' },
    async submitTicket() {
      this.ticketLoading = true; this.ticketError = '';
      try {
        await PORTAL_Tickets.add(this.ticketForm);
        this.dialogTicket = false;
        this.ticketForm = { DocumentTypeID: '', PriorityTypesID: null, UserMessage: '' };
        this.dataSource.reload();
      } catch (e: any) {
        this.ticketError = e?.response?.data?.message ?? e?.response?.data ?? 'Eroare la trimitere.';
      } finally {
        this.ticketLoading = false;
      }
    }
  }
})
</script>

<style scoped>
.tichete-page { padding: 16px; width: 100%; }

.hero {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%);
  border-radius: 18px; padding: 22px 26px; color: #fff; box-shadow: 0 10px 30px rgba(255,87,34,.28); margin-bottom: 18px;
}
.hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; opacity: .85; }
.hero-title { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 4px 0 2px; }
.hero-sub { font-size: 13px; opacity: .92; margin: 0; }
.btn-nou { background: #fff !important; color: #ff5722 !important; font-weight: 700; border-radius: 22px; text-transform: none; letter-spacing: 0; }

.grid-card { background: #fff; border: 1px solid #eef1f6; border-radius: 16px; box-shadow: 0 1px 3px rgba(15,23,42,.05); overflow: hidden; }
.grid-head { display: flex; align-items: center; padding: 15px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; font-weight: 700; color: #1e293b; }
.nr-badge { font-family: 'Roboto Mono', monospace; font-weight: 700; font-size: 12.5px; color: #ff5722; background: #fff1ec; padding: 3px 9px; border-radius: 8px; }
.text-muted { color: #94a3b8; font-style: italic; }
.stare-pill { display: inline-flex; align-items: center; gap: 7px; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; }
.stare-pill .dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
.stare-pill.deschis { background: #eff6ff; color: #2563eb; } .stare-pill.deschis .dot { background: #2563eb; }
.stare-pill.inchis { background: #f1f5f9; color: #64748b; } .stare-pill.inchis .dot { background: #94a3b8; }

.dlg-card { overflow: hidden; }
.dlg-head { display: flex; align-items: center; gap: 12px; background: linear-gradient(120deg,#ff5722,#ff8a5c); color: #fff; padding: 16px 18px; }
.dlg-ic { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,.2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dlg-title { font-size: 16px; font-weight: 800; line-height: 1.15; }
.dlg-desc { font-size: 12px; opacity: .9; margin-top: 2px; }
.dlg-foot { padding: 14px 20px; border-top: 1px solid #eef1f6; }
</style>
